import { Loader2, Search, Star } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { getDetails } from "../api";

export default function AddModal({ onAdd, onClose, PLATFORMS, PLATFORM_COLORS }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualData, setManualData] = useState({
    title: "",
    year: "",
    type: "movie",
    poster: "",
    platform: "",
  });
  const [platform, setPlatform] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const dropdownRef = useRef(null);

  async function searchContent(q) {
    setSearching(true);
    setError("");
    setResults([]);
    setSelected(null);
    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`);
      if (!res.ok) throw new Error("Error en la búsqueda");
      const data = await res.json();
      setResults(data.results || []);
    } catch (err) {
      setError("No se pudo buscar: " + err.message);
    } finally {
      setSearching(false);
    }
  }

  useEffect(() => {
    if (!query) {
      setResults([]);
      setSelected(null);
      return;
    }
    const timeout = setTimeout(() => searchContent(query), 500);
    return () => clearTimeout(timeout);
  }, [query]);

  // ✅ FIX problema 1: llama a /api/search/details/:tmdbId para obtener TODOS los datos
  async function handleSelect(item) {
    setResults([]);
    setShowManual(false);
    setError("");
    setLoadingDetails(true);
    setSelected(null);

    const itemType =
      item.type || (item.media_type === "tv" ? "series" : "movie");

    try {
      const details = await getDetails(item.tmdb_id || item.id, itemType);
      setPlatform(details.platform || "");
      setSelected({ ...details, type: itemType });
    } catch (err) {
      setError("Información básica — no se obtuvieron todos los detalles");
      setSelected({
        title: item.title || item.name || "",
        year: item.year || "",
        type: itemType,
        poster: item.poster || null,
        platform: "",
      });
    } finally {
      setLoadingDetails(false);
    }
  }

  function typeLabel(item) {
    const t = item.type || (item.media_type === "tv" ? "series" : "movie");
    return (
      {
        series: "Serie",
        movie: "Película",
        documentary: "Documental",
        anime: "Anime",
        short: "Corto",
      }[t] || "Película"
    );
  }

  // ✅ FIX problema 3: cerrar al pulsar el fondo
  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className="modal-bg fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70"
      onClick={handleBackdropClick}
    >
      {/* ✅ FIX problema 3: bottom sheet en móvil, modal centrado en desktop */}
      <div className="bg-cinema-card w-full sm:max-w-lg sm:rounded-xl rounded-t-2xl shadow-2xl relative flex flex-col max-h-[90vh]">
        {/* ── CABECERA FIJA: buscador ── */}
        <div className="flex-shrink-0 p-5 pb-3 border-b border-cinema-border">
          {/* Indicador drag móvil */}
          <div className="w-10 h-1 bg-steel-600 rounded-full mx-auto mb-4 sm:hidden" />

          <button
            className="absolute top-4 right-4 text-steel-400 hover:text-white z-10 p-1"
            onClick={onClose}
            aria-label="Cerrar"
          >
            ✕
          </button>

          <div className="relative" ref={dropdownRef}>
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-steel-400"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelected(null);
              }}
              className="w-full bg-cinema-bg border border-cinema-border rounded-xl pl-9 pr-10 py-3 text-white placeholder-steel-500 focus:outline-none focus:border-cinema-accent transition-all"
              placeholder="Escribe un título..."
              autoFocus
            />
            {searching && (
              <Loader2
                size={16}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-cinema-accent animate-spin"
              />
            )}

            {/* ✅ FIX problema 1: tipo correcto usando item.type del backend */}
            {results.length > 0 && (
              <div className="absolute left-0 right-0 mt-1 bg-cinema-bg border border-cinema-border rounded-xl shadow-2xl max-h-52 overflow-y-auto z-50">
                {results.map((item) => (
                  <div
                    key={`${item.tmdb_id || item.id}-${item.type || item.media_type}`}
                    onClick={() => handleSelect(item)}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-steel-800 active:bg-steel-700 cursor-pointer transition-colors border-b border-cinema-border last:border-b-0"
                  >
                    {item.poster ? (
                      <img
                        src={item.poster}
                        alt=""
                        className="w-8 h-12 object-cover rounded flex-shrink-0"
                      />
                    ) : (
                      <div className="w-8 h-12 bg-steel-700 rounded flex items-center justify-center text-sm flex-shrink-0">
                        {item.type === "series" || item.media_type === "tv"
                          ? "📺"
                          : "🎬"}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">
                        {item.title || item.name}
                      </p>
                      {/* ✅ typeLabel usa item.type ya mapeado por el backend */}
                      <p className="text-steel-400 text-xs">
                        {typeLabel(item)}
                        {item.year && ` · ${item.year}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── CUERPO SCROLLABLE ── */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-5 pt-4">
          {/* Cargando detalles */}
          {loadingDetails && (
            <div className="flex flex-col items-center justify-center py-10 text-steel-400 gap-3">
              <Loader2 size={28} className="animate-spin text-cinema-accent" />
              <span className="text-sm">Obteniendo información completa…</span>
            </div>
          )}

          {/* Formulario manual */}
          {!loadingDetails && showManual && (
            <form
              className="animate-fade-in"
              onSubmit={async (e) => {
                e.preventDefault();
                setSaving(true);
                setError("");
                try {
                  await onAdd({ ...manualData });
                  setShowManual(false);
                } catch (err) {
                  setError("Error al guardar: " + err.message);
                  setSaving(false);
                }
              }}
            >
              <div className="mb-4">
                <label className="block text-sm font-medium text-steel-300 mb-1">
                  Título
                </label>
                <input
                  type="text"
                  className="w-full bg-cinema-bg border border-cinema-border rounded-xl px-3 py-2.5 text-white"
                  value={manualData.title}
                  onChange={(e) =>
                    setManualData((d) => ({ ...d, title: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-steel-300 mb-1">
                    Año
                  </label>
                  <input
                    type="number"
                    className="w-full bg-cinema-bg border border-cinema-border rounded-xl px-3 py-2.5 text-white"
                    value={manualData.year}
                    onChange={(e) =>
                      setManualData((d) => ({ ...d, year: e.target.value }))
                    }
                    min="1900"
                    max="2100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-steel-300 mb-1">
                    Tipo
                  </label>
                  <select
                    className="w-full bg-cinema-bg border border-cinema-border rounded-xl px-3 py-2.5 text-white"
                    value={manualData.type}
                    onChange={(e) =>
                      setManualData((d) => ({ ...d, type: e.target.value }))
                    }
                  >
                    <option value="movie">Película</option>
                    <option value="series">Serie</option>
                    <option value="short">Corto</option>
                    <option value="documentary">Documental</option>
                    <option value="anime">Anime</option>
                  </select>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-steel-300 mb-1">
                  Póster (URL)
                </label>
                <input
                  type="text"
                  className="w-full bg-cinema-bg border border-cinema-border rounded-xl px-3 py-2.5 text-white"
                  value={manualData.poster}
                  onChange={(e) =>
                    setManualData((d) => ({ ...d, poster: e.target.value }))
                  }
                  placeholder="https://..."
                />
              </div>
              <div className="mb-5">
                <label className="block text-sm font-medium text-steel-300 mb-2">
                  Plataforma
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-2 gap-2">
                  {PLATFORMS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() =>
                        setManualData((d) => ({
                          ...d,
                          platform: d.platform === p ? "" : p,
                        }))
                      }
                      className={`flex items-center gap-1.5 px-2 py-2 rounded-xl text-xs font-medium transition-all border ${
                        manualData.platform === p
                          ? "border-cinema-accent text-white"
                          : "border-cinema-border text-steel-300 hover:border-steel-500"
                      }`}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ background: PLATFORM_COLORS[p] }}
                      />
                      <span className="truncate">{p}</span>
                    </button>
                  ))}
                </div>
              </div>
              {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-cinema-accent hover:bg-cinema-accent-hover disabled:bg-steel-600 text-white font-semibold py-3 rounded-xl transition-all"
                >
                  {saving ? "Guardando..." : "Añadir a mi lista"}
                </button>
                <button
                  type="button"
                  className="flex-1 bg-steel-700 hover:bg-steel-600 text-white font-semibold py-3 rounded-xl transition-all"
                  onClick={() => setShowManual(false)}
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}

          {/* Título seleccionado con detalles completos */}
          {!loadingDetails && !showManual && selected && (
            <div className="animate-fade-in">
              <div className="flex gap-4 mb-4">
                {selected.poster ? (
                  <img
                    src={selected.poster}
                    alt="Poster"
                    className="w-16 h-24 sm:w-20 sm:h-32 object-cover rounded-lg flex-shrink-0"
                  />
                ) : (
                  <div className="w-16 h-24 bg-steel-700 rounded-lg flex items-center justify-center text-2xl flex-shrink-0">
                    {selected.type === "series" ? "📺" : "🎬"}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold mb-1 leading-tight">
                    {selected.title}
                  </h3>
                  <div className="flex items-center gap-2 text-steel-400 text-xs flex-wrap mt-1">
                    {selected.year && <span>{selected.year}</span>}
                    <span>{typeLabel(selected)}</span>
                    {selected.runtime && <span>{selected.runtime}</span>}
                    {selected.imdb_rating && (
                      <span className="flex items-center gap-0.5 text-yellow-400 font-medium">
                        <Star size={10} fill="currentColor" />
                        {selected.imdb_rating}
                      </span>
                    )}
                  </div>
                  {selected.genre && (
                    <p className="text-steel-500 text-xs mt-1 line-clamp-1">
                      {selected.genre}
                    </p>
                  )}
                  {selected.director && (
                    <p className="text-steel-500 text-xs mt-0.5">
                      Dir: {selected.director}
                    </p>
                  )}
                </div>
              </div>

              {selected.plot && selected.plot !== "N/A" && (
                <p className="text-steel-400 text-xs mb-4 leading-relaxed line-clamp-2">
                  {selected.plot}
                </p>
              )}

              {/* ✅ FIX problema 3: 3 columnas en móvil */}
              <label className="block text-sm font-medium text-steel-300 mb-2">
                Plataforma
                {selected.platform && (
                  <span className="ml-2 text-cinema-accent text-xs font-normal">
                    (detectada: {selected.platform})
                  </span>
                )}
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-2 gap-2 mb-5">
                {PLATFORMS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPlatform(platform === p ? "" : p)}
                    className={`flex items-center gap-1.5 px-2 py-2 rounded-xl text-xs font-medium transition-all border ${
                      platform === p
                        ? "border-cinema-accent text-white"
                        : "border-cinema-border text-steel-300 hover:border-steel-500"
                    }`}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ background: PLATFORM_COLORS[p] }}
                    />
                    <span className="truncate">{p}</span>
                  </button>
                ))}
              </div>

              {error && <p className="text-yellow-400 text-sm mb-3">{error}</p>}

              {/* ✅ FIX problema 1: enviar selected completo con todos los datos de la API */}
              <button
                onClick={async () => {
                  setSaving(true);
                  setError("");
                  try {
                    await onAdd({
                      ...selected,
                      platform: platform || selected.platform || "",
                    });
                  } catch (err) {
                    setError("Error al guardar: " + err.message);
                    setSaving(false);
                    return;
                  }
                  setSaving(false);
                }}
                disabled={saving}
                className="w-full bg-cinema-accent hover:bg-cinema-accent-hover disabled:bg-steel-600 text-white font-semibold py-3 rounded-xl transition-all shadow-lg shadow-cinema-accent/20"
              >
                {saving ? "Guardando..." : "Añadir a mi lista"}
              </button>
            </div>
          )}

          {/* Pantalla inicial vacía */}
          {!loadingDetails && !showManual && !selected && (
            <div className="text-center text-steel-400 py-6">
              <div className="text-4xl mb-3">🎬</div>
              <p className="text-sm">
                Busca una serie o película, o{" "}
                <button
                  className="text-cinema-accent underline underline-offset-2"
                  onClick={() => setShowManual(true)}
                >
                  añade manualmente
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
